import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GraduationCap, Eye } from 'lucide-react';
import { bulkAdmit, errText, type ClassGroup, type Cycle } from '@/api/admissions';
import { Card, Btn, Field, input, Notice, Pill, Empty } from './ui';

type Row = { id: number; reference: string; name: string; class?: string; percentage?: number | null; reason?: string; admission_no?: string };

export function AdmitTab({ cycle, groups }: { cycle: Cycle | undefined; groups: ClassGroup[] }) {
  const qc = useQueryClient();
  const [groupKey, setGroupKey] = useState(groups[0]?.key ?? '');
  const [rule, setRule] = useState<'passed' | 'min_score' | 'all_paid'>('passed');
  const [min, setMin] = useState(60);
  const [arms, setArms] = useState<number[]>([]);
  const [distribute, setDistribute] = useState<'even' | 'fill'>('even');
  const [preview, setPreview] = useState<{ admit: Row[]; skipped: Row[] } | null>(null);
  const [done, setDone] = useState<{ admitted: Row[]; failed: Row[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const group = groups.find((g) => g.key === groupKey);
  const body = () => ({ cycle_id: cycle?.id, class_group: group?.name, rule, min_score: rule === 'min_score' ? min : undefined, class_ids: arms, distribute });

  const previewM = useMutation({ mutationFn: () => bulkAdmit(body()), onSuccess: (r: any) => { setPreview(r); setDone(null); setErr(null); }, onError: (e) => setErr(errText(e)) });
  const commitM = useMutation({
    mutationFn: () => bulkAdmit({ ...body(), commit: true }),
    onSuccess: (r: any) => { setDone(r); setPreview(null); qc.invalidateQueries({ queryKey: ['adm-apps'] }); qc.invalidateQueries({ queryKey: ['adm-summary'] }); },
    onError: (e) => setErr(errText(e)),
  });

  if (!cycle) return <Empty icon={GraduationCap}>Create an admission cycle first.</Empty>;
  if (!cycle.has_default_password) return <Notice tone="info">Set the default student password in the Settings tab first. Every admitted applicant gets a reg number and that password to sign in to the school app.</Notice>;

  return (
    <div className="space-y-4">
      <Card title="Admit many applicants at once" sub="Best scores first. Admitted applicants become students of the arm they are placed in.">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Class">
              <select value={groupKey} onChange={(e) => { setGroupKey(e.target.value); setArms([]); setPreview(null); }} className={input}>
                {groups.map((g) => <option key={g.key} value={g.key}>{g.name}</option>)}
              </select>
            </Field>
            <Field label="Who to admit">
              <select value={rule} onChange={(e) => { setRule(e.target.value as any); setPreview(null); }} className={input}>
                <option value="passed">Everyone who passed the entrance exam</option>
                <option value="min_score">Everyone scoring at least…</option>
                <option value="all_paid">Everyone who paid (no exam needed)</option>
              </select>
            </Field>
          </div>
          {rule === 'min_score' && <Field label="Minimum score (%)"><input type="number" min={0} max={100} value={min} onChange={(e) => { setMin(Number(e.target.value)); setPreview(null); }} className={`${input} sm:w-40`} /></Field>}
          <Field label="Put them into these arms">
            <div className="flex flex-wrap gap-2">
              {(group?.arms ?? []).map((a) => {
                const on = arms.includes(a.id);
                return <button type="button" key={a.id} onClick={() => { setArms(on ? arms.filter((x) => x !== a.id) : [...arms, a.id]); setPreview(null); }}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${on ? 'bg-green-600 text-white border-green-600' : 'bg-white border-slate-300 text-slate-700'}`}>{a.name}{a.capacity ? ` (${a.capacity})` : ''}</button>;
              })}
            </div>
          </Field>
          {arms.length > 1 && (
            <Field label="How to spread them">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {([['even', 'Evenly across the arms', 'Best, 2nd best… go to arm 1, arm 2, arm 1…'], ['fill', 'Fill one arm first', 'Fill the first arm to capacity, then the next']] as const).map(([k, t, h]) => (
                  <button type="button" key={k} onClick={() => { setDistribute(k); setPreview(null); }} className={`text-left border rounded-lg px-3 py-2 ${distribute === k ? 'border-green-600 bg-green-50' : 'border-slate-200'}`}>
                    <span className="block text-sm font-semibold">{t}</span><span className="block text-xs text-slate-500">{h}</span>
                  </button>
                ))}
              </div>
            </Field>
          )}
          {err && <Notice tone="err" onClose={() => setErr(null)}>{err}</Notice>}
          <div className="flex flex-col sm:flex-row gap-2">
            <Btn kind="ghost" disabled={!arms.length || previewM.isPending} onClick={() => previewM.mutate()}><Eye className="w-4 h-4" />{previewM.isPending ? 'Checking…' : 'Preview'}</Btn>
            {preview && preview.admit.length > 0 && (
              <Btn disabled={commitM.isPending} onClick={() => commitM.mutate()}><GraduationCap className="w-4 h-4" />{commitM.isPending ? 'Admitting…' : `Admit ${preview.admit.length} applicant(s)`}</Btn>
            )}
          </div>
        </div>
      </Card>

      {preview && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ListCard title={`Will be admitted (${preview.admit.length})`} rows={preview.admit} right={(r) => <span className="text-right"><span className="block text-sm font-semibold">{r.class}</span>{r.percentage != null && <span className="text-xs text-slate-500">{r.percentage}%</span>}</span>} />
          <ListCard title={`Not included (${preview.skipped.length})`} rows={preview.skipped} right={(r) => <Pill tone="slate">{r.reason}</Pill>} />
        </div>
      )}
      {done && (
        <>
          <Notice tone="ok">{done.admitted.length} applicant(s) admitted. Each got a reg number and the default password on their dashboard and by SMS.</Notice>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ListCard title="Admitted" rows={done.admitted} right={(r) => <span className="text-right"><span className="block text-sm font-semibold">{r.class}</span><span className="text-xs font-mono text-slate-500">{r.admission_no}</span></span>} />
            {done.failed.length > 0 && <ListCard title="Could not admit" rows={done.failed} right={(r) => <span className="text-xs text-red-600 text-right">{r.reason}</span>} />}
          </div>
        </>
      )}
    </div>
  );
}

function ListCard({ title, rows, right }: { title: string; rows: Row[]; right: (r: Row) => React.ReactNode }) {
  return (
    <Card title={title} pad={false}>
      {rows.length === 0 ? <p className="p-4 text-sm text-slate-500">None.</p> : (
        <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
          {rows.map((r) => <li key={r.id} className="px-4 py-2.5 flex items-center justify-between gap-3"><span className="min-w-0"><span className="block text-sm font-medium text-slate-900 truncate">{r.name}</span><span className="text-xs text-slate-500">{r.reference}</span></span>{right(r)}</li>)}
        </ul>
      )}
    </Card>
  );
}
